import { useAuth } from "@assetwatch/auth";
import { Alert, Select, Stack, Text, Title } from "@assetwatch/ui";
import { Card } from "@assetwatch/ui/experimental";
import { useMutation, useQuery } from "@tanstack/react-query";
import { post } from "aws-amplify/api";
import type { TFunction } from "i18next";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getLanguagePreferenceValue,
  getSupportedLanguages,
  LANGUAGE_PREFERENCE,
  resolvePreferredLanguageCode,
  type SupportedLanguage,
} from "../lib/languagePreferences";
import { useFeatureFlags } from "./LaunchDarkly/useFeatureFlags";

async function updatePreferenceWithValue(preferenceValue: string) {
  await post({
    apiName: "apiVeroUser",
    path: "/update",
    options: {
      body: {
        meth: "updatePreferenceWithValue",
        preferenceID: LANGUAGE_PREFERENCE,
        pVal: preferenceValue,
      },
    },
  }).response;
}

function getLanguageOptionLabel(
  language: SupportedLanguage,
  t: TFunction,
): string {
  switch (language.code) {
    case "en":
      return t("enterprise.settings.languageOptionEnglish");
    case "fr":
      return t("enterprise.settings.languageOptionFrench");
    case "es":
      return t("enterprise.settings.languageOptionSpanish");
    case "ja":
      return t("enterprise.settings.languageOptionJapanese");
    default:
      return language.name;
  }
}

export function EnterpriseSettingsPanelLanguage() {
  const { t, i18n } = useTranslation();
  const { user, refreshAuth } = useAuth();
  const { i18nEnabled: i18nEnabledFlag } = useFeatureFlags();
  const ldClient = useLDClient();
  const i18nEnabled = ldClient
    ? ldClient.variation("i18nEnabled", i18nEnabledFlag)
    : i18nEnabledFlag;
  const { data: supportedLanguages = [] } = useQuery({
    queryKey: ["enterprise-supported-languages"],
    queryFn: getSupportedLanguages,
    enabled: i18nEnabled,
    staleTime: Infinity,
  });
  const languagePreferenceValue = getLanguagePreferenceValue(user?.preferences);
  const selectedLanguagePreference = resolvePreferredLanguageCode(
    languagePreferenceValue,
    supportedLanguages,
    supportedLanguages[0]?.code ?? "en",
  );
  const [languageSelectValue, setLanguageSelectValue] = useState(
    selectedLanguagePreference,
  );
  const [languageSaveError, setLanguageSaveError] = useState<string | null>(
    null,
  );

  const {
    mutateAsync: mutateLanguagePreferenceAsync,
    isPending: isSavingLanguage,
  } = useMutation({
    mutationFn: updatePreferenceWithValue,
  });

  useEffect(() => {
    setLanguageSelectValue(selectedLanguagePreference);
  }, [selectedLanguagePreference]);

  if (!i18nEnabled || supportedLanguages.length === 0) {
    return null;
  }

  return (
    <Card data-testid="settings-language-card">
      <Stack gap={12}>
        <Title size="md" as="h2">
          {t("enterprise.settings.languageTitle")}
        </Title>
        <Text size="sm" tone="subtle">
          {t("enterprise.settings.languageDescription")}
        </Text>
        <Select
          data-testid="preference-panel-language-select"
          label={t("enterprise.settings.languageTitle")}
          value={languageSelectValue}
          disabled={isSavingLanguage}
          onChange={async (nextLanguageCode: string | null) => {
            if (!nextLanguageCode) {
              return;
            }

            const previousLanguageCode = languageSelectValue;
            setLanguageSaveError(null);
            setLanguageSelectValue(nextLanguageCode);
            const language = supportedLanguages.find(
              (lang) => lang.code === nextLanguageCode,
            );
            try {
              await mutateLanguagePreferenceAsync(
                language ? String(language.lid) : nextLanguageCode,
              );
              // Apply the language change BEFORE refreshAuth() so that
              // every re-render triggered by the auth context update
              // (including <Trans> components in the session debug card)
              // already sees the new language. Calling it after refreshAuth()
              // left a window where components rendered with the new auth
              // context but the old i18n language, freezing <Trans> output
              // until a second render pass that not all subscribers received.
              await i18n.changeLanguage(nextLanguageCode);
              await refreshAuth();
            } catch {
              setLanguageSelectValue(previousLanguageCode);
              // Revert the eager language change so the UI stays coherent
              // if the save or auth refresh failed.
              await i18n.changeLanguage(previousLanguageCode);
              setLanguageSaveError(t("enterprise.settings.languageSaveError"));
            }
          }}
          options={supportedLanguages.map((lang) => ({
            value: lang.code,
            label: getLanguageOptionLabel(lang, t),
          }))}
        />
        {languageSaveError ? (
          <Alert tone="negative" title={languageSaveError} />
        ) : null}
      </Stack>
    </Card>
  );
}
